<script>
import moment from "moment";

const TYPES = {
  success: { icon: "mdi-check-circle", color: "var(--v-success-base)" },
  error: { icon: "mdi-close-circle", color: "var(--v-error-base)" },
  warning: { icon: "mdi-alert", color: "var(--v-warning-base)" },
  info: { icon: "mdi-information", color: "var(--v-info-base)" },
};

export default {
  props: {
    animationDuration: {
      type: Number,
      default: 1500,
    },
    duration: {
      type: Number,
      default: 4000,
    },
    message: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'info',
      // Sem validator: o back-end pode enviar tipos ainda não mapeados aqui
      // (não é um bug) — typeConfig() abaixo já cai no fallback 'info' sem
      // precisar gerar warning no console pra cada valor desconhecido.
    },
  },
  data() {
    return {
      expiryDate: null,
      intervalId: null,
      mouse: false,
      remainDuration: this.duration,
      show: true,
      timeoutId: null,
      timerProgress: 100,
    };
  },
  computed: {
    typeConfig() {
      return TYPES[this.type] || TYPES.info;
    },
    computedClass() {
      return `notification ${this.show ? "show" : "hide"}`;
    },
    computedStyle() {
      return {
        "--animation-duration": `${this.animationDuration}ms`,
        "--notification-color": this.typeConfig.color,
      };
    },
    timerInterval() {
      return Math.ceil(this.duration / 400);
    },
  },
  methods: {
    startTimer() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        clearInterval(this.intervalId);
      }

      this.expiryDate = moment().add(this.remainDuration, 'milliseconds');
      this.timeoutId = setTimeout(this.close, this.remainDuration);
      this.intervalId = setInterval(() => this.timerProgress -= 0.25, this.timerInterval);
    },
    pauseTimer() {
      if (!this.timeoutId) {
        return;
      }

      this.remainDuration = this.expiryDate.diff(moment(), 'milliseconds');
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
      clearInterval(this.intervalId);
      this.intervalId = null;
    },
    clearTimer() {
      if (!this.timeoutId) {
        return;
      }

      this.remainDuration = this.duration;
      this.timerProgress = 100;
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
      clearInterval(this.intervalId);
      this.intervalId = null;
    },
    close() {
      this.show = false;
      this.clearTimer();
      setTimeout(() => this.$emit("close"), this.animationDuration);
    },
    dismiss() {
      this.close();
      this.$emit("dismiss");
    },
  },
  mounted() {
    setTimeout(this.startTimer, this.animationDuration);
  },
}
</script>

<template>
  <div :class="computedClass" :style="computedStyle" @mouseenter="pauseTimer" @mouseleave="startTimer">
    <v-icon class="type-icon" :color="typeConfig.color" size="22">{{ typeConfig.icon }}</v-icon>
    <div class="message" v-html="message" />
    <v-btn class="close" :ripple="false" icon x-small @click="dismiss">
      <v-icon size="16">mdi-close</v-icon>
    </v-btn>
    <div class="progress" :style="{ width: `${timerProgress}%` }" />
  </div>
</template>

<style lang="scss" scoped>
.notification {
  align-items: flex-start;
  background-color: #ffffff;
  border-left: 4px solid var(--notification-color);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  color: #1C1C44;
  display: flex;
  flex-direction: row;
  font-size: 0.8125rem;
  gap: 0.5rem;
  margin: 0.3125rem 0.3125rem 0;
  max-width: 500px;
  min-width: 280px;
  overflow: hidden;
  padding: 0.75rem 0.875rem;
  position: relative;
  width: fit-content;

  &.show {
    @keyframes slide-in {
      0% {
        opacity: 0;
        transform: translateX(+102%);
      }

      100% {
        opacity: 1;
        transform: translateX(0%);
      }
    }

    animation-name: slide-in;
    animation-duration: var(--animation-duration);
    animation-timing-function: ease-out;
  }

  &.hide {
    @keyframes slide-out {
      0% {
        opacity: 1;
        transform: translateX(0%);
      }

      100% {
        opacity: 0;
        transform: translateX(+102%);
      }
    }

    animation-name: slide-out;
    animation-duration: var(--animation-duration);
    animation-timing-function: ease-in;
  }

  .type-icon {
    flex-shrink: 0;
    margin-top: 0.0625rem;
  }

  .message {
    flex: 1;
    line-height: 1.4;
    padding-right: 1rem;
    word-break: break-word;
  }

  .close {
    flex-shrink: 0;
    margin: -0.25rem -0.25rem 0 0;
    width: min-content;

    .v-icon::before {
      color: rgba(28, 28, 68, 0.5);
      text-shadow: none;
      opacity: 1;
    }

    &:hover .v-icon::before {
      color: rgba(28, 28, 68, 0.85);
    }
  }

  .progress {
    background-color: var(--notification-color);
    position: absolute;
    bottom: 0;
    left: 0;
    height: 0.1875rem;
    opacity: 0.6;
    transition: width 0.1s linear;
  }
}
</style>
